import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  EuiComboBox,
  EuiComboBoxOptionOption,
  EuiFlexItem,
  EuiFormHelpText,
} from "@elastic/eui";
import {
  AnketaResourcesEnum,
  isValidEmail,
  KeycloakUser,
  useAnketaAuth,
  useApi,
  useToast,
} from "../../../../";
import { ComplexQuestionEditorProps } from "../../../";
import { useAnketa } from "../../context_provider";
import { useParams } from "react-router";
import { useAnketaI18n } from "src/internationalization";

/** Split a stored value into its single recipient tokens. */
const splitTokens = (raw: string | undefined | null): string[] =>
  (raw ?? "")
    .split(/[,;\s]+/)
    .map((token) => token.trim())
    .filter(Boolean);

/** Case-insensitive de-duplication that keeps the first spelling. */
const dedupe = (tokens: string[]): string[] => {
  const seen = new Set<string>();
  return tokens.filter((token) => {
    const key = token.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * Editor for the "Customer - Update notification recipients" field: a true multi-value
 * input - every entry is one recipient of the wave update mails.
 *
 * Unlike the generic user picker it never depends on the Keycloak lookup to
 * show what is stored: the saved value is parsed and rendered immediately, so
 * an empty customer organization (or a slow/failing request) can no longer make
 * existing recipients invisible - which previously caused the next save to
 * silently reduce the list to the single freshly typed address.
 *
 * Stored format: a comma-joined list of e-mail addresses. Legacy values that
 * hold Keycloak user ids are kept verbatim (the backend parser resolves them,
 * see helper/parser/opsanio.ts) and only get a readable label once the user
 * list is loaded. Nothing here provisions Keycloak users.
 */
export const UpdateRecipientsEditor = ({
  question,
  setValue,
  invalid,
}: ComplexQuestionEditorProps): JSX.Element => {
  const { keycloak } = useAnketaAuth();
  const ctx = useAnketa();
  const { Get } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();
  const params = useParams();

  const [users, setUsers] = useState<KeycloakUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(true);
  const [tokens, setTokens] = useState<string[]>([]);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const initialized = useRef<boolean>(false);

  // Show the stored recipients right away - independent of the Keycloak fetch.
  useEffect(() => {
    if (initialized.current) {
      return;
    }
    initialized.current = true;
    setTokens(dedupe(splitTokens(question.fact.getStrValue(ctx.i18nContext))));
  }, [question, ctx]);

  // Suggestions: the customer organizations assigned to this questionnaire.
  // Purely optional - a failure only costs the dropdown, not the input.
  useEffect(() => {
    if (typeof keycloak?.token !== "string") {
      return;
    }
    let cancelled = false;
    const load = async (): Promise<void> => {
      const { data, success } = await Get<Array<KeycloakUser>>({
        path: `v2/${AnketaResourcesEnum.questionnaires}/${params.catalogId}/${params.questionnaireId}/organizations/customers`,
        responseType: "json",
        token: keycloak.token,
      });
      if (cancelled) {
        return;
      }
      setUsers(success && Array.isArray(data) ? data : []);
      setIsLoadingUsers(false);
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token, params.catalogId, params.questionnaireId]);

  const labelForToken = useCallback(
    (token: string): string => {
      const lower = token.toLowerCase();
      const user = users.find(
        (candidate) =>
          candidate.id === token || candidate.email?.toLowerCase() === lower,
      );
      if (!user) {
        return token;
      }
      const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
      return name ? `${name} (${user.email})` : user.email;
    },
    [users],
  );

  const selectedOptions = useMemo<EuiComboBoxOptionOption<string>[]>(
    () =>
      tokens.map((token) => ({
        label: labelForToken(token),
        value: token,
      })),
    [tokens, labelForToken],
  );

  // Offer every known customer contact that is not selected yet. Selecting one
  // stores its e-mail address, never its Keycloak id, so the recipient survives
  // a user being renamed or removed in Keycloak.
  const options = useMemo<EuiComboBoxOptionOption<string>[]>(() => {
    const taken = new Set(tokens.map((token) => token.toLowerCase()));
    return users
      .filter((user) => isValidEmail(user.email ?? ""))
      .filter(
        (user) =>
          !taken.has(user.email.toLowerCase()) && !taken.has(user.id ?? ""),
      )
      .map((user) => {
        const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
        return {
          label: name ? `${name} (${user.email})` : user.email,
          value: user.email,
        };
      });
  }, [users, tokens]);

  const commit = useCallback(
    (next: string[]): void => {
      const cleaned = dedupe(next);
      setTokens(cleaned);
      setValue(cleaned.join(","));
      setIsInvalid(false);
    },
    [setValue],
  );

  const onChange = (selected: EuiComboBoxOptionOption<string>[]): void => {
    commit(selected.map((option) => option.value ?? option.label));
  };

  // Free-typed addresses. `delimiter` makes a pasted "a@x.de, b@y.de" arrive
  // here once per address, so bulk paste works as well as typing.
  const onCreateOption = (searchValue: string = ""): boolean => {
    const value = searchValue.trim();
    if (!isValidEmail(value)) {
      setIsInvalid(true);
      addToast({
        title: translate("anketa.questionnaire.editor.user.createFailed.title", {
          defaultMessage: "Creation failed",
        }),
        color: "danger",
        text: translate("anketa.questionnaire.editor.user.createFailed.text", {
          defaultMessage: "Input is not a valid e-mail address!",
        }),
      });
      return false;
    }
    commit([...tokens, value.toLowerCase()]);
    return true;
  };

  return (
    <EuiFlexItem grow style={{ minWidth: "200px" }}>
      <EuiComboBox
        placeholder={translate(
          "anketa.questionnaire.editor.updateRecipients.placeholder",
          { defaultMessage: "Add e-mail addresses" },
        )}
        options={options}
        selectedOptions={selectedOptions}
        onChange={onChange}
        onCreateOption={onCreateOption}
        customOptionText={translate(
          "anketa.questionnaire.editor.updateRecipients.customOption",
          { defaultMessage: "Add {searchValue} as a recipient" },
        )}
        delimiter=","
        isClearable={true}
        isLoading={isLoadingUsers}
        isDisabled={question.readonly}
        isInvalid={invalid || isInvalid}
      />
      <EuiFormHelpText>
        {tokens.length === 0
          ? translate("anketa.questionnaire.editor.updateRecipients.empty", {
              defaultMessage:
                "No recipients - no update notification will be sent.",
            })
          : translate("anketa.questionnaire.editor.updateRecipients.count", {
              values: { count: tokens.length },
              defaultMessage: "{{count}} recipient(s) will be notified.",
            })}
      </EuiFormHelpText>
    </EuiFlexItem>
  );
};
